/**
 * Utility functions for formatting values based on locale
 */

/**
 * Format currency based on locale
 * @param amount - The amount to format
 * @param locale - The locale (e.g., 'pt-BR', 'en')
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, locale: string = 'en'): string => {
  if (locale === 'pt-BR' || locale === 'pt') {
    // Brazilian Real format: R$ XX,XX
    return `R$ ${amount.toFixed(2).replace('.', ',')}`;
  }
  // Default English format: BRL XX.XX
  return `BRL ${amount.toFixed(2)}`;
};

/**
 * Format date based on locale
 * @param date - The date to format
 * @param locale - The locale (e.g., 'pt-BR', 'en')
 * @returns Formatted date string
 */
export const formatDate = (date: Date, locale: string = 'en'): string => {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

/**
 * Format number with locale-specific separators
 * @param value - The number to format
 * @param locale - The locale (e.g., 'pt-BR', 'en')
 * @returns Formatted number string
 */
export const formatNumber = (value: number, locale: string = 'en'): string => {
  return new Intl.NumberFormat(locale).format(value);
};

/**
 * Format percentage
 * @param value - The percentage value (0-100)
 * @param locale - The locale (e.g., 'pt-BR', 'en')
 * @param decimals - Number of decimal places
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number,
  locale: string = 'en',
  decimals: number = 1
): string => {
  return `${value.toFixed(decimals)}%`;
};
